import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Site, page } from '@cloudsign/shared';
import { api } from './api';
import { useCapabilitiesQuery } from './session';

const SitePage = page(Site);

export interface SiteContextValue {
  /** Active sites this person can see (all of them, or only their assigned ones). */
  sites: Site[];
  /** The chosen site, or the first one when nothing valid is remembered. Null when there are no sites. */
  site: Site | null;
  siteId: string | null;
  setSiteId: (id: string) => void;
  loading: boolean;
  error: unknown;
}

const EMPTY: SiteContextValue = { sites: [], site: null, siteId: null, setSiteId: () => undefined, loading: false, error: null };
const SiteContext = createContext<SiteContextValue>(EMPTY);

const storageKey = (tenantId: string): string => `cloudsign.site.${tenantId}`;

function readStored(tenantId: string): string | null {
  try {
    return window.localStorage.getItem(storageKey(tenantId));
  } catch {
    return null;
  }
}

function writeStored(tenantId: string, siteId: string): void {
  try {
    window.localStorage.setItem(storageKey(tenantId), siteId);
  } catch {
    // Storage can be blocked; the choice then lasts until the page reloads.
  }
}

/**
 * Loads the person's active sites and remembers the one they are working on (per organisation).
 * Wrap pages that need a site; the top bar picker reads it. Does nothing in the platform area or when
 * the sites module is off.
 */
export function SiteProvider({ children }: { children: ReactNode }) {
  const caps = useCapabilitiesQuery().data;
  const tenantId = caps?.tenant?.id ?? null;
  const sitesOn = caps?.area === 'tenant' && (caps.modules.some((m) => m.key === 'sites' && m.enabled) ?? false);
  const q = useQuery({
    queryKey: ['sites', 'picker', tenantId],
    queryFn: () => api.get('/sites', { query: { status: 'active', pageSize: 200 }, schema: SitePage }),
    enabled: sitesOn,
    staleTime: 60_000,
  });
  const [chosen, setChosen] = useState<{ tenantId: string; siteId: string } | null>(null);

  const sites = useMemo(() => {
    const all = q.data?.items ?? [];
    const user = caps?.user;
    const visible = user && user.siteScope === 'assigned' ? all.filter((s) => user.siteIds.includes(s.id)) : all;
    return visible.filter((s) => s.status === 'active');
  }, [q.data, caps?.user]);

  const siteId = useMemo(() => {
    if (!tenantId || sites.length === 0) return null;
    const wanted = chosen?.tenantId === tenantId ? chosen.siteId : readStored(tenantId);
    return sites.find((s) => s.id === wanted)?.id ?? sites[0]?.id ?? null;
  }, [sites, chosen, tenantId]);

  const setSiteId = useCallback(
    (id: string) => {
      if (!tenantId) return;
      writeStored(tenantId, id);
      setChosen({ tenantId, siteId: id });
    },
    [tenantId],
  );

  const value = useMemo<SiteContextValue>(
    () => ({
      sites,
      site: sites.find((s) => s.id === siteId) ?? null,
      siteId,
      setSiteId,
      loading: sitesOn && q.isPending,
      error: q.error,
    }),
    [sites, siteId, setSiteId, sitesOn, q.isPending, q.error],
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

/** The site the person is working on, and the list to switch between. */
export function useSite(): SiteContextValue {
  return useContext(SiteContext);
}
